import React, { memo, useCallback, useEffect, useState } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { useUser } from 'Context/User';
import { UserModel } from 'Containers/User/Models/UserModel';
import { DownloadReportModal } from 'Components/ReportsAndDocuments/Reports/DownloadReportModal';
import { useDispatch, useSelector } from 'react-redux';
import { employeesAttachedSelector } from 'Containers/Crew/selectors';
import { download, formatDate } from 'Utils/helpers';
import { listProjectMembers, setEmployeesAttached } from 'Containers/Crew/actions';
import { PurpleButton } from 'Components/Button';
import { Icon } from 'Components/Icons';
import { ActionButtonWrapper } from 'Containers/ActionButtonWrapper';
import {
  EsignatureWrapper,
  Esignature,
  EsignatureTable,
  ContractFormModal,
  ShareEsignatureModal,
} from 'Components/Esignature';

import { useHistory, useParams } from 'react-router-dom';
import { DeleteEsignatureModal } from 'Components/ReportsAndDocuments/Reports/DeleteEsignatureModal';
import { projectSelector } from 'Containers/RocketScan/selectors';
import { userFeatureFlagsSelector } from 'Containers/User/selector';
import {
  deleteEsignature,
  getEsignatureApi,
  listEsignatureApi,
  resetDeleteEsignature,
  signCompanyContract,
  shareEsignature,
  setEsignatureShared,
} from 'Containers/Esignature/action';
import {
  eSignatureCreatedSelector,
  eSignatureDeletedSelector,
  eSignatureSelector,
  eSignatureSharingSelector,
  eSignatureSharedSelector,
  emailErrorSelector,
} from './selector';

import { listPdfFormSubmissions, deletePdfFormSubmission, setSubmissionDeleted, SET_PDF_FORM_SUBMISSIONS } from 'Containers/PdfFormTemplates/actions';
import { pdfFormSubmissionsSelector, pdfFormSubmissionDeletedSelector, fetchingPdfFormTemplatesSelector } from 'Containers/PdfFormTemplates/selectors';
import { SendPdfFormFromProjectModal } from 'Containers/PdfFormTemplates/SendPdfFormFromProjectModal';
import { Table, TableBody, TableColumn, TableHeader, TableRow, Th } from 'Components/Table';

import classes from './esignature.module.css';

function EsignatureContainer() {
  const { companies }: UserModel = useUser();
  const dispatch = useDispatch();
  const { id: userId } = useUser();
  const history = useHistory();
  const { projectId }: any = useParams();

  // local variables
  const [isOpenSelectMembersModal, setIsOpenSelectMembersModal] = useState(false);
  const [selectedSign, setSelectedSign] = useState(undefined);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showContractFormModal, setContractFormModal] = useState(false);
  const [showPdfFormModal, setShowPdfFormModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eSignToDelete, setEsignToDelete] = useState(null);
  const [email, setEmail] = useState('');

  // selectors
  const employeesAttached = useSelector(employeesAttachedSelector, areEqual);
  const project = useSelector(projectSelector, areEqual);
  const eSignatureDeleted = useSelector(eSignatureDeletedSelector, areEqual);
  const eSignatureCreated = useSelector(eSignatureCreatedSelector, areEqual);
  const eSignatureSharing = useSelector(eSignatureSharingSelector, areEqual);
  const eSignatureShared = useSelector(eSignatureSharedSelector, areEqual);
  const eSignatures = useSelector(eSignatureSelector);

  // PDF form submissions
  const pdfSubmissions = useSelector(pdfFormSubmissionsSelector, areEqual);
  const pdfSubmissionDeleted = useSelector(pdfFormSubmissionDeletedSelector, areEqual);
  const fetchingPdfSubmissions = useSelector(fetchingPdfFormTemplatesSelector, areEqual);
  const pdfSubmissionData = pdfSubmissions?.data || [];

  // feature flag selector
  const { hideDeleteButton } = useSelector(userFeatureFlagsSelector, areEqual);

  /*
   * share functions
   * */
  const errors = {
    email: useSelector(emailErrorSelector, areEqual),
  };

  const onChangeEmail = useCallback(({ target: { value } }: any) => {
    setEmail(value);
  }, []);

  const onFormSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      const { id } = selectedSign;

      dispatch(shareEsignature(id, { email }));
    },
    [email, selectedSign]
  );

  // api
  const getProjectMembers = useCallback(() => {
    dispatch(listProjectMembers(project.id, userId));
  }, [project]);

  // initial api call
  useEffect(() => {
    if (project) {
      getProjectMembers();
    }
  }, [project]);

  useEffect(() => {
    if (employeesAttached) {
      setIsOpenSelectMembersModal(false);
    }
  }, [employeesAttached]);

  useEffect(() => {
    if (!isOpenSelectMembersModal && employeesAttached) {
      getProjectMembers();
    }

    return () => {
      if (!isOpenSelectMembersModal && employeesAttached) {
        dispatch(setEmployeesAttached(false));
      }
    };
  }, [isOpenSelectMembersModal, employeesAttached]);

  useEffect(() => {
    if (eSignatureShared) {
      setShowShareModal(false);
      dispatch(setEsignatureShared(false));
    }

    return () => {
      if (eSignatureShared) {
        dispatch(setEsignatureShared(false));
      }
    };
  }, [eSignatureShared]);

  const getCompanySigns = useCallback(() => {
    if (companies.length > 0 && projectId) {
      const [company] = companies;
      const { id: companyId } = company;
      // const { id: projectId } = project;
      dispatch(listEsignatureApi(companyId, projectId));
    }
  }, [companies, projectId]);

  const onDownloadIconClick = useCallback(
    (esign: any) => {
      setSelectedSign(esign);
      setShowDownloadModal(true);
    },
    [eSignatures]
  );

  const onEditIconClick = useCallback(
    (esign: any) => {
      const { id } = esign;
      history.replace(`/projects/${projectId}/documents/${id}`);
    },
    [selectedSign, projectId]
  );

  const getRegeneratedSignature = async (signature: any) => {
    setSelectedSign(signature);
    const { url }: any = signature;
    await download(url, 'E-SIGN', setLoading);
    getCompanySigns();
  };

  const onDownloadButtonClick = useCallback(async () => {
    if (selectedSign) {
      setLoading(true);
      const { url } = selectedSign;
      if (url) {
        await download(url, 'E-SIGN', setLoading);
      } else {
        // Unsigned contract: fetch the full record on demand (GET /e-signatures/{id})
        // instead of reading content/signature off the list row, which the trimmed
        // list response no longer carries (WEBAPP-BUG-016 / MONGOOSE-BUG-050).
        const { id } = selectedSign;
        const record: any = await dispatch(getEsignatureApi(id));
        if (record) {
          const { content, signature } = record;
          dispatch(signCompanyContract(id, signature, content, getRegeneratedSignature));
        } else {
          setLoading(false);
        }
      }
    }
  }, [selectedSign, getCompanySigns]);

  const onShareIconClick = useCallback((esig: any) => {
    setSelectedSign(esig);
    setShowShareModal(true);
  }, []);

  const onDeleteButtonClick = useCallback(
    (esign: any) => {
      dispatch(resetDeleteEsignature());
      const { id } = esign;
      dispatch(deleteEsignature(id));
      setShowDeleteModal(false);
    },
    [selectedSign]
  );

  const onCancelButtonClick = useCallback((e: any) => {
    e.preventDefault();
    setTimeout(() => setSelectedSign(undefined), 500);
    setShowDownloadModal(false);
    setShowShareModal(false);
    setContractFormModal(false);
    setShowDeleteModal(false);
  }, []);

  const onShowDeleteModal = useCallback((esign: any) => {
    setEsignToDelete(esign);
    setShowDeleteModal(true);
  }, []);

  useEffect(() => {
    getCompanySigns();
  }, [showContractFormModal, eSignatureDeleted, eSignatureCreated]);

  // Clear stale submissions and fetch for this project
  useEffect(() => {
    dispatch({ type: SET_PDF_FORM_SUBMISSIONS, payload: { data: [] } });
    if (projectId) {
      dispatch(listPdfFormSubmissions(1, Number(projectId)) as any);
    }
  }, [projectId]);

  useEffect(() => {
    if (pdfSubmissionDeleted) {
      dispatch(setSubmissionDeleted(false) as any);
      if (projectId) {
        dispatch(listPdfFormSubmissions(1, Number(projectId)) as any);
      }
    }
  }, [pdfSubmissionDeleted]);

  const onDeletePdfSubmission = useCallback((sub: any) => {
    if (window.confirm(`Delete PDF form submission for ${sub.client_name}?`)) {
      dispatch(deletePdfFormSubmission(sub.id) as any);
    }
  }, []);

  const onPdfSubmissionCreated = useCallback(() => {
    if (projectId) {
      dispatch(listPdfFormSubmissions(1, Number(projectId)) as any);
    }
  }, [projectId]);

  return (
    <EsignatureWrapper>
      <Esignature>
        <div className={`d-flex justify-content-start align-items-center ${classes.eSignContent}`}>
          <h2 className={classes.eSignHeading}>Documents</h2>
          <PurpleButton className={classes.addButton} onClick={() => setContractFormModal(true)}>
            Add Form +
          </PurpleButton>
          <PurpleButton className={classes.addButton} onClick={() => setShowPdfFormModal(true)}>
            Add PDF Form +
          </PurpleButton>
        </div>
        <EsignatureTable
          esignatures={{ meta: { total: 1 }, data: eSignatures }}
          canDelete={!hideDeleteButton}
          onDownloadIconClick={onDownloadIconClick}
          onShareIconClick={onShareIconClick}
          onShowDeleteModal={onShowDeleteModal}
          onEditIconClick={onEditIconClick}
        />

        <div className={classes.pdfSubmissionsSection}>
          <h3 className={classes.pdfSubmissionsHeading}>PDF Forms</h3>
          {pdfSubmissionData.length > 0 ? (
            <Table className="table">
              <TableHeader>
                <TableRow>
                  <Th>Form Name</Th>
                  <Th>Client</Th>
                  <Th>Status</Th>
                  <Th>Date</Th>
                  <Th />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pdfSubmissionData.map((sub: any) => (
                  <TableRow key={sub.id}>
                    <TableColumn>{sub.template?.name || '—'}</TableColumn>
                    <TableColumn>
                      <div>{sub.client_name}</div>
                      <div style={{ fontSize: 12, color: '#777185' }}>{sub.client_email}</div>
                    </TableColumn>
                    <TableColumn>{sub.status}</TableColumn>
                    <TableColumn>{formatDate(sub.signed_at || sub.created_at, 'PP')}</TableColumn>
                    <TableColumn>
                      <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        {sub.signed_url && (
                          <a href={sub.signed_url} target="_blank" rel="noopener noreferrer">
                            <Icon type="download" />
                          </a>
                        )}
                        {!hideDeleteButton && (
                          <ActionButtonWrapper onClick={() => onDeletePdfSubmission(sub)} item={sub}>
                            <Icon type="trash" />
                          </ActionButtonWrapper>
                        )}
                      </div>
                    </TableColumn>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : !fetchingPdfSubmissions ? (
            <p style={{ color: '#777185', fontSize: 14, padding: '12px 0' }}>
              No PDF form submissions yet. Click &quot;Add PDF Form +&quot; to send a form to a client.
            </p>
          ) : null}
        </div>
      </Esignature>

      <DownloadReportModal
        loading={loading}
        isOpen={showDownloadModal}
        onDownloadButtonClick={onDownloadButtonClick}
        modalCloseClick={onCancelButtonClick}
        reportName="Signed Document"
      />
      <ShareEsignatureModal
        loading={eSignatureSharing}
        esignatureName={selectedSign?.contract_form?.name ?? 'Untitled Form'}
        email={email}
        isOpen={showShareModal}
        formErrors={errors}
        modalCloseClick={onCancelButtonClick}
        onChangeEmail={onChangeEmail}
        onFormSubmit={onFormSubmit}
      />

      {!hideDeleteButton && (
        <DeleteEsignatureModal
          loading={loading}
          esign={eSignToDelete}
          reportName=""
          showDeleteModal={showDeleteModal}
          onDeleteButtonClick={onDeleteButtonClick}
          onCancelButtonClick={onCancelButtonClick}
          modalCloseClick={onCancelButtonClick}
        />
      )}
      <ContractFormModal
        isOpen={showContractFormModal}
        setContractFormModal={setContractFormModal}
        modalCloseClick={onCancelButtonClick}
      />
      <SendPdfFormFromProjectModal
        isOpen={showPdfFormModal}
        projectId={Number(projectId)}
        onClose={() => setShowPdfFormModal(false)}
        onSubmissionCreated={onPdfSubmissionCreated}
      />
    </EsignatureWrapper>
  );
}

const EsignatureContainerMemo = memo(EsignatureContainer, areEqual);

export { EsignatureContainerMemo as Esignature };
